import type { Block, ComposerDocument } from '../types';
import { CATALOG_BY_TYPE, defaultProps, getDefinition } from '../components/catalog';
import { newId } from './tree';

/* ==========================================================================
   Tree <-> plain HTML.
   --------------------------------------------------------------------------
   Persistence rule: documents live on disk as semantic HTML. Each block is
   wrapped in a host element (article / section / div / li) carrying:
     data-component="<type>"      – maps back to a catalog entry
     data-prop-<key>="<value>"    – serialized scalar props
   Rich-text props are inlined as HTML inside `<div data-rich="<key>">`.
   The HTML is intentionally minimal and renders OK on its own (e.g. paste
   it into any HTML viewer and it still looks like a worksheet, modulo the
   editor chrome).
   ========================================================================== */

function escapeAttr(v: unknown): string {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function serializeProp(key: string, value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return ` data-prop-${key}="${value ? '1' : '0'}"`;
  return ` data-prop-${key}="${escapeAttr(value)}"`;
}

function serializeBlock(block: Block): string {
  const def = getDefinition(block.type);
  if (!def) return '';
  const richKeys = def.props.filter((p) => p.type === 'rich').map((p) => p.key);
  const scalarKeys = def.props.filter((p) => p.type !== 'rich').map((p) => p.key);

  const propAttrs = scalarKeys
    .map((k) => serializeProp(k, block.props[k]))
    .join('');

  const richInner = richKeys
    .map((k) => `<div data-rich="${k}">${(block.props[k] as string) ?? ''}</div>`)
    .join('');

  const childrenHtml = (block.children || []).map(serializeBlock).join('');

  return `<div data-component="${block.type}" data-block-id="${block.id}"${propAttrs}>${richInner}${childrenHtml}</div>`;
}

export function treeToHtml(tree: Block[]): string {
  return tree.map(serializeBlock).join('\n');
}

function parseScalarProps(el: Element, type: string): Record<string, unknown> {
  const def = CATALOG_BY_TYPE[type];
  if (!def) return {};
  const out: Record<string, unknown> = { ...defaultProps(type) };
  for (const p of def.props) {
    if (p.type === 'rich') continue;
    const raw = el.getAttribute(`data-prop-${p.key}`);
    if (raw === null) continue;
    if (p.type === 'number') out[p.key] = Number(raw);
    else if (p.type === 'boolean') out[p.key] = raw === '1' || raw === 'true';
    else out[p.key] = raw;
  }
  return out;
}

function parseBlock(el: Element): Block | null {
  const type = el.getAttribute('data-component');
  if (!type || !CATALOG_BY_TYPE[type]) return null;
  const id = el.getAttribute('data-block-id') || newId();
  const props = parseScalarProps(el, type);

  /* Rich text */
  el.querySelectorAll(':scope > [data-rich]').forEach((rich) => {
    const k = rich.getAttribute('data-rich');
    if (k) props[k] = rich.innerHTML;
  });

  const children: Block[] = [];
  el.querySelectorAll(':scope > [data-component]').forEach((child) => {
    const b = parseBlock(child);
    if (b) children.push(b);
  });

  const def = CATALOG_BY_TYPE[type];
  const block: Block = { id, type, props };
  if (def.tier === 'container') block.children = children;
  return block;
}

export function htmlToTree(html: string): Block[] {
  const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, 'text/html');
  const root = doc.getElementById('root');
  if (!root) return [];
  const out: Block[] = [];
  root.querySelectorAll(':scope > [data-component]').forEach((el) => {
    const b = parseBlock(el);
    if (b) out.push(b);
  });
  return out;
}

/* ==========================================================================
   Whole-document serialization for export.
   --------------------------------------------------------------------------
   `documentToHtml()` produces a self-contained HTML file referencing the
   shared print.css + chosen theme so the output renders correctly when
   opened standalone (e.g. saved to disk, shared, hosted on Cloudflare).
   ========================================================================== */
export function documentToStandaloneHtml(doc: ComposerDocument, baseHref = ''): string {
  const themeLink = doc.theme
    ? `<link rel="stylesheet" href="${baseHref}${doc.theme}">`
    : '';
  const overrides: string[] = [];
  if (doc.colorOverrides?.accent) overrides.push(`--accent:${doc.colorOverrides.accent}`);
  if (doc.colorOverrides?.ink) overrides.push(`--ink:${doc.colorOverrides.ink}`);
  if (doc.colorOverrides?.paper) overrides.push(`--paper:${doc.colorOverrides.paper}`);
  const styleAttr = overrides.length ? ` style="${overrides.join(';')}"` : '';

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeAttr(doc.name)}</title>
<link rel="stylesheet" href="${baseHref}print.css">
${themeLink}
</head>
<body>
<article class="sheet"${styleAttr} data-document-id="${doc.id}" data-theme="${escapeAttr(doc.theme)}">
${treeToHtml(doc.tree)}
</article>
</body>
</html>`;
}
